import { EncryptStorage } from "encrypt-storage";
import { format } from "date-fns";
import { id, enUS } from "date-fns/locale";
import { twMerge as cn } from "tailwind-merge";
import CryptoJS from "crypto-js";

const numberWithCommas = (number) => {
  return number?.toString()?.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
};

const regexThousandSeparator = (number) => {
  if (number?.split(".")?.length > 2) {
    number = number?.replace(/\.+$/, "");
  }
  var regex = /^[0-9]*\.?[0-9]*$/;
  let val = number.match(regex);
  return val || null;
};

const encryptStorage = new EncryptStorage(import.meta.env.VITE_STORAGE, {
  storageType: "localStorage",
  encAlgorithm: "Rabbit",
});

const fetchEncryptId = (id) => {
  try {
    const params = id?.toString() || "";
    const code = JSON.parse(atob(params));
    return code;
  } catch (e) {
    return false;
  }
};

const convertDate = ({ date, displayFormat = "dd-MM-yyyy", language = false }) => {
  if (!date || date === "0000-00-00" || date === "0000-00-00 00:00:00") {
    return "";
  }
  let defDate = new Date(date);
  if (isNaN(defDate.getTime())) {
    return "";
  }
  let formatLanguage = language ? id : enUS;
  return format(defDate, displayFormat, { locale: formatLanguage });
};

const convertToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const getMonths = () => ({
  1: "Januari",
  2: "Februari",
  3: "Maret",
  4: "April",
  5: "Mei",
  6: "Juni",
  7: "Juli",
  8: "Agustus",
  9: "September",
  10: "Oktober",
  11: "November",
  12: "Desember",
});

const getYears = (
  from = 2024,
  to = new Date().getFullYear() + 1
) => {
  const years = {};
  for (let i = from; i <= to; i += 1) {
    years[i] = i;
  }
  return years;
};

const getMonthName = (index) => getMonths()[index];
const isEmpty = (params) => {
  return [null, "", " ", "null", undefined].includes(params);
};

const pageNumber = (page, limit) => {
  return parseInt((page - 1) * limit);
};

const API = {
  academics: import.meta.env.VITE_URL_AUTH,
  staff: import.meta.env.VITE_URL_STAFF,
  form: import.meta.env.VITE_URL_FORM,
  log: import.meta.env.VITE_URL_LOG,
  sso: import.meta.env.VITE_URL_AUTH,
  ssoClient: import.meta.env.VITE_URL_SSO_CLIENT,
};

const DynamicFormOption = [
  {
    label: "Text",
    value: "text",
  },
  {
    label: "Number",
    value: "number",
  },
  {
    label: "Email",
    value: "email",
  },
  {
    label: "Radio",
    value: "radio",
  },
  {
    label: "Checkbox",
    value: "checkbox",
  },
  {
    label: "Dropdown",
    value: "dropdown",
  },
];

const mongoObjectId = () => {
  var timestamp = ((new Date().getTime() / 1000) | 0).toString(16);
  return (
    timestamp +
    "xxxxxxxxxxxxxxxx"
      .replace(/[x]/g, function () {
        return ((Math.random() * 16) | 0).toString(16);
      })
      .toLowerCase()
  );
};

function encryptData(value) {
  const key = import.meta.env.VITE_STORAGE;
  const encrypted = CryptoJS.AES.encrypt(value, key).toString();
  return encodeURIComponent(encrypted);
}

function decryptData(value) {
  const key = import.meta.env.VITE_STORAGE;
  const decodedValue = decodeURIComponent(value)
  const decrypted = CryptoJS.AES.decrypt(decodedValue, key).toString(
    CryptoJS.enc.Utf8
  );
  return decrypted;
}

function redirectToSso() {
  const baseUrl = window.location.origin;
  const redirectUrl = `${API.ssoClient}/login?oauthId=${encryptData(baseUrl)}`;
  window.location.href = redirectUrl;
}

const formatNumber = (value) => {
  if (!value) return "";
  const parts = value.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ","); // Add commas to the integer part
  return parts.length > 1 ? parts.join(".") : parts[0]; // Keep the decimal part
};

const parseNumber = (formattedValue) => {
  // Remove invalid characters, allow only one dot
  const numeric = formattedValue.replace(/[^0-9.]/g, ""); // Keep digits and dot
  const [integer, ...decimals] = numeric.split(".");
  return decimals.length > 0
    ? `${integer}.${decimals.join("")}` // Reconstruct number if more than one dot exists
    : numeric;
};

const isBetweenDate = ({ currentDate = "", startDate = "", endDate = "" }) => {
  const dateObj = currentDate ? new Date(currentDate) : new Date();
  const startObj = startDate ? new Date(startDate) : new Date();
  const endObj = endDate ? new Date(endDate) : new Date();
  return dateObj >= startObj && dateObj <= endObj;
};

const allowSubUnitList = ["A11", "E03", "F05", "G06"];

function angkaTerbilang(angka) {
  angka = Math.abs(angka);
  const baca = ["", "satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "delapan", "sembilan", "sepuluh", "sebelas"];

  if (angka < 12) {
      return " " + baca[angka];
  } else if (angka < 20) {
      return angkaTerbilang(angka - 10) + " belas";
  } else if (angka < 100) {
      return angkaTerbilang(Math.floor(angka / 10)) + " puluh" + angkaTerbilang(angka % 10);
  } else if (angka < 200) {
      return " seratus" + angkaTerbilang(angka - 100);
  } else if (angka < 1000) {
      return angkaTerbilang(Math.floor(angka / 100)) + " ratus" + angkaTerbilang(angka % 100);
  } else if (angka < 2000) {
      return " seribu" + angkaTerbilang(angka - 1000);
  } else if (angka < 1000000) {
      return angkaTerbilang(Math.floor(angka / 1000)) + " ribu" + angkaTerbilang(angka % 1000);
  } else if (angka < 1000000000) {
      return angkaTerbilang(Math.floor(angka / 1000000)) + " juta" + angkaTerbilang(angka % 1000000);
  } else if (angka < 1000000000000) {
      return angkaTerbilang(Math.floor(angka / 1000000000)) + " milyar" + angkaTerbilang(angka % 1000000000);
  } else if (angka < 1000000000000000) {
      return angkaTerbilang(Math.floor(angka / 1000000000000)) + " triliun" + angkaTerbilang(angka % 1000000000000);
  }
}

const setGroup = (arrData) => {
    // Polyfill for Object.groupBy yang mempertahankan urutan asli
    const groupByPreserveOrder = (items, callback) => {
      if (!items) return {};

      const groups = {};
      const groupOrder = []; // Untuk menyimpan urutan kemunculan grup pertama kali

      items.forEach((item, index) => {
        const key = callback(item, index);
        if (!groups[key]) {
          groups[key] = [];
          groupOrder.push(key); // Simpan urutan kemunculan grup
        }
        groups[key].push(item);
      });

      return { groups, groupOrder };
    };

    // Original logic with modified polyfill
    const { groups, groupOrder } = groupByPreserveOrder(
      arrData || [],
      ({ group }) => group
    );

    // Kembalikan hasil dalam urutan kemunculan grup pertama kali
    const result = groupOrder.map((key) => groups[key]);

    return result || [];
  };

const removeNumbering = (string = "") => {
  return string?.replace(/^\d+(?:\.\d+)*\.?\s*/, '');
}

const getYearBasedOnCutoff = (cutoffDay = 1, cutoffMonth = 12) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // Months are 0-indexed (0 = January)
  const currentDay = now.getDate();

  if (
    currentMonth < cutoffMonth || 
    (currentMonth === cutoffMonth && currentDay < cutoffDay)
  ) {
    return parseInt(currentYear - 1).toString(); // Previous year if before cutoff
  }
  return currentYear.toString(); // Current year if on or after cutoff
}

const formatFileSize = (bytes) => {
  if (!bytes) return '...';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};


export {
  numberWithCommas,
  fetchEncryptId,
  convertDate,
  cn,
  getMonths,
  getMonthName,
  getYears,
  regexThousandSeparator,
  convertToBase64,
  mongoObjectId,
  encryptData,
  decryptData,
  isEmpty,
  parseNumber,
  formatNumber,
  pageNumber,
  angkaTerbilang,
  setGroup,
  removeNumbering,
  getYearBasedOnCutoff,
  formatFileSize,
  encryptStorage,
  API,
  allowSubUnitList,
  DynamicFormOption,
  redirectToSso,
  isBetweenDate
};
